/**
 * Config Migration Utility
 * Automatically updates stored config to match code defaults
 */

export function migrateConfig(): void {
    try {
        console.log('[ConfigMigration] Starting migration...');
        
        // Update luca_global_config
        const globalConfigRaw = localStorage.getItem('luca_global_config');
        if (globalConfigRaw) {
            const config = JSON.parse(globalConfigRaw);
            let updated = false;
            
            // Migrate old gemini-2.0-flash to gemini-2.5-flash-lite
            if (config.freeModel === 'gemini-2.0-flash') {
                config.freeModel = 'gemini-2.5-flash-lite';
                updated = true;
                console.log('[ConfigMigration] Updated freeModel: gemini-2.0-flash → gemini-2.5-flash-lite');
            }
            
            if (config.premiumModel === 'gemini-2.0-flash') {
                config.premiumModel = 'gemini-2.5-flash-lite';
                updated = true;
                console.log('[ConfigMigration] Updated premiumModel: gemini-2.0-flash → gemini-2.5-flash-lite');
            }
            
            if (config.maxModel === 'gemini-2.0-flash') {
                config.maxModel = 'gemini-2.5-flash-lite';
                updated = true;
                console.log('[ConfigMigration] Updated maxModel: gemini-2.0-flash → gemini-2.5-flash-lite');
            }
            
            // Migrate Token Economy v2.0 (December 2024)
            if (config.freeTokenLimit === 5000) {
                config.freeTokenLimit = 2000;
                updated = true;
                console.log('[ConfigMigration] Updated freeTokenLimit: 5000 → 2000');
            }
            
            if (config.premiumTokenLimit === 50000) {
                config.premiumTokenLimit = 15000;
                updated = true;
                console.log('[ConfigMigration] Updated premiumTokenLimit: 50000 → 15000');
            }
            
            if (config.maxTokenLimit === 200000) {
                config.maxTokenLimit = 50000;
                updated = true;
                console.log('[ConfigMigration] Updated maxTokenLimit: 200000 → 50000');
            }
            
            if (config.standardRefreshRate === 800) {
                config.standardRefreshRate = 200;
                updated = true;
                console.log('[ConfigMigration] Updated standardRefreshRate: 800 → 200');
            }
            
            if (config.premiumRefreshRate === 5000) {
                config.premiumRefreshRate = 1500;
                updated = true;
                console.log('[ConfigMigration] Updated premiumRefreshRate: 5000 → 1500');
            }
            
            if (config.maxRefreshRate === 20000) {
                config.maxRefreshRate = 5000;
                updated = true;
                console.log('[ConfigMigration] Updated maxRefreshRate: 20000 → 5000');
            }
            
            if (updated) {
                localStorage.setItem('luca_global_config', JSON.stringify(config));
                console.log('[ConfigMigration] ✅ Global config migrated successfully');
            } else {
                console.log('[ConfigMigration] Global config already up to date');
            }
        }
        
        // Update luca_central_system
        const systemConfigRaw = localStorage.getItem('luca_central_system');
        if (systemConfigRaw) {
            const systemConfig = JSON.parse(systemConfigRaw);
            let updated = false;
            
            if (systemConfig.defaultApiModel === 'gemini-2.0-flash') {
                systemConfig.defaultApiModel = 'gemini-2.5-flash-lite';
                updated = true;
                console.log('[ConfigMigration] Updated defaultApiModel: gemini-2.0-flash → gemini-2.5-flash-lite');
            }
            
            if (systemConfig.fallbackApiModel === 'gemini-2.0-flash') {
                systemConfig.fallbackApiModel = 'gemini-2.5-flash-lite';
                updated = true;
                console.log('[ConfigMigration] Updated fallbackApiModel: gemini-2.0-flash → gemini-2.5-flash-lite');
            }
            
            if (updated) {
                localStorage.setItem('luca_central_system', JSON.stringify(systemConfig));
                console.log('[ConfigMigration] ✅ System config migrated successfully');
            }
        }
        
        // Update central config
        const centralConfigRaw = localStorage.getItem('luca_central_config');
        if (centralConfigRaw) {
            const centralConfig = JSON.parse(centralConfigRaw);
            let updated = false;
            
            // Update system models
            if (centralConfig.system) {
                if (centralConfig.system.defaultApiModel === 'gemini-2.0-flash') {
                    centralConfig.system.defaultApiModel = 'gemini-2.5-flash-lite';
                    updated = true;
                }
                if (centralConfig.system.fallbackApiModel === 'gemini-2.0-flash') {
                    centralConfig.system.fallbackApiModel = 'gemini-2.5-flash-lite';
                    updated = true;
                }
            }
            
            // Update rank configs
            if (centralConfig.ranks && Array.isArray(centralConfig.ranks)) {
                centralConfig.ranks.forEach((rank: any) => {
                    if (rank.defaultModel === 'gemini-2.0-flash') {
                        rank.defaultModel = 'gemini-2.5-flash-lite';
                        updated = true;
                        console.log(`[ConfigMigration] Updated rank ${rank.id} model`);
                    }
                });
            }
            
            if (updated) {
                localStorage.setItem('luca_central_config', JSON.stringify(centralConfig));
                console.log('[ConfigMigration] ✅ Central config migrated successfully');
            }
        }
        
        console.log('[ConfigMigration] ✅ Migration complete');
        
    } catch (error) {
        console.error('[ConfigMigration] Error during migration:', error);
    }
}

/**
 * Check if migration is needed
 */
export function needsMigration(): boolean {
    try {
        const globalConfigRaw = localStorage.getItem('luca_global_config');
        if (globalConfigRaw) {
            const config = JSON.parse(globalConfigRaw);
            return config.freeModel === 'gemini-2.0-flash' || 
                   config.premiumModel === 'gemini-2.0-flash' ||
                   config.maxModel === 'gemini-2.0-flash' ||
                   config.freeTokenLimit === 5000 ||
                   config.premiumTokenLimit === 50000 ||
                   config.maxTokenLimit === 200000 ||
                   config.standardRefreshRate === 800 ||
                   config.premiumRefreshRate === 5000 ||
                   config.maxRefreshRate === 20000;
        }
        return false;
    } catch {
        return false;
    }
}
